// 3rd-party
import React from 'react';

// Components
import ShareContentModal from './ShareContentModal';
import GeneratedLinkModal from './GeneratedLinkModal';
import ShareEmailConfirmationModal from './ShareEmailConfirmationModal';
import { Input } from '../inputs';
import { CardButton, PrimaryLink, CloseButton } from '../buttons';
import Modal from '@/views/components/modals/AccessibleModal';

// Constants and types
import { SHARED_CONTENT_METHOD, SHARED_CONTENT_TYPES } from '@/utils/constants';
import SharedComparisonInfos from '@/types/shared-comparison-infos';
import Nullable from '@/types/nullable';

// Assets
import capintelLogo from '@/images/capintel-logo-small.svg';
import emailIcon from '@/images/email-icon.png';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '../i18n/FormattedMessageV2';

type Props = {
  showModal: boolean;
  type: SHARED_CONTENT_TYPES;
  comparisonIsDirty?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onShareComparison: (infos: SharedComparisonInfos) => Promise<Nullable<string>>;
  onSaveChanges?: () => Promise<boolean>;
};

type State = {
  showShareContentModal: boolean;
  showGeneratedLinkModal: boolean;
  showShareEmailConfirmationModal: boolean;
  url?: string;
  shareMethod: SHARED_CONTENT_METHOD;
  isSaving?: boolean;
  isSaved?: boolean;
};

class ShareAllModal extends React.PureComponent<Props, State> {
  private inputRef = React.createRef<Input>();

  readonly state: State = {
    showShareContentModal: false,
    showGeneratedLinkModal: false,
    showShareEmailConfirmationModal: false,
    shareMethod: SHARED_CONTENT_METHOD.LINK,
    isSaving: false,
    isSaved: false,
  };

  copyText = () => {
    if (this.inputRef.current) {
      this.inputRef.current.selectAll();
      document.execCommand('copy');
    }
  };

  onSuccess = (url: Nullable<string>) => {
    this.closeShareContentModal();
    if (url) {
      this.setState({ url, showGeneratedLinkModal: true });
    } else {
      this.setState({ showShareEmailConfirmationModal: true });
    }
  };

  closeShareContentModal = () => this.setState({ showShareContentModal: false });

  closeGeneratedLinkModal = () => this.setState({ showGeneratedLinkModal: false, url: undefined });

  closeShareEmailConfirmationModal = () => this.setState({ showShareEmailConfirmationModal: false });

  shareViaEmail = () => {
    const { onConfirm } = this.props;
    this.setState({ shareMethod: SHARED_CONTENT_METHOD.EMAIL, showShareContentModal: true });
    onConfirm();
  };

  shareViaLink = () => {
    const { onConfirm } = this.props;
    this.setState({ shareMethod: SHARED_CONTENT_METHOD.LINK, showShareContentModal: true });
    onConfirm();
  };

  handleOnExited = () => {
    this.setState({ isSaving: false, isSaved: false });
  };

  handleSaveChanges = async () => {
    const { onSaveChanges } = this.props;

    if (!onSaveChanges) return;

    this.setState({ isSaved: false, isSaving: true });
    const comparisonSaved = await onSaveChanges();

    if (!comparisonSaved) {
      return this.setState({ isSaving: false, isSaved: false });
    }

    this.setState({ isSaving: false, isSaved: true });
  };

  render() {
    const { type, showModal, comparisonIsDirty, onCancel, onShareComparison } = this.props;
    const { shareMethod, showShareContentModal, showGeneratedLinkModal, showShareEmailConfirmationModal, url, isSaving, isSaved } = this.state;

    return (
      <>
        <Modal show={showModal} onHide={onCancel} onExited={this.handleOnExited} className="share-all-modal" dialogClassName="modal-dialog-centered">
          <Modal.Body>
            <CloseButton onClick={onCancel} />

            <div>
              {/* Provisional fix. Pending migration to library modal. */}
              <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="headerXs">
                <FormattedMessage id="ShareAllModal.Header" />
              </Typography>
              <FormattedMessage tagName="p" id={`ShareAllModal.${comparisonIsDirty ? 'BodyHasChanges' : 'Body'}`} />
            </div>

            <div className="share-options-wrapper">
              <CardButton
                onClick={this.shareViaLink}
                iconUrl={emailIcon}
                title={formatMessage('ShareAllModal.Title.Link')}
                subtitle={formatMessage('ShareAllModal.Text.Link')}
                disabled={isSaving}
              />

              <CardButton
                onClick={this.shareViaEmail}
                iconUrl={capintelLogo}
                title={formatMessage('ShareAllModal.Title.Email')}
                subtitle={formatMessage('ShareAllModal.Text.Email')}
                disabled={isSaving}
              />
            </div>

            {comparisonIsDirty && !isSaved && (
              <div className="alert alert-warning" role="alert">
                <i className="icon-ds-warning" />
                <div>
                  <FormattedMessage tagName="p" id="ShareAllModal.Warning" />
                  <PrimaryLink onClick={this.handleSaveChanges} loading={isSaving}>
                    <FormattedMessage id={!isSaving ? 'Comparison.SaveChanges' : 'Common.Saving'} />
                  </PrimaryLink>
                </div>
              </div>
            )}

            {isSaved && (
              <div className="alert alert-success" role="alert">
                <i className="icon-circle-checkmark" />
                <FormattedMessage tagName="p" id="ShareAllModal.Success" />
              </div>
            )}
          </Modal.Body>
        </Modal>

        <ShareContentModal
          onSubmit={onShareComparison}
          showModal={showShareContentModal}
          onCancel={this.closeShareContentModal}
          onSuccess={this.onSuccess}
          type={type}
          shareMethod={shareMethod}
        />

        <GeneratedLinkModal
          showModal={showGeneratedLinkModal}
          onConfirm={this.closeGeneratedLinkModal}
          onCancel={this.closeGeneratedLinkModal}
          type={type}
          url={url}
        />

        <ShareEmailConfirmationModal
          onCancel={this.closeShareEmailConfirmationModal}
          onConfirm={this.closeShareEmailConfirmationModal}
          showModal={showShareEmailConfirmationModal}
        />
      </>
    );
  }
}

export default ShareAllModal;
